import { AmChartsFrequencySetData } from '@rds/rds-base-ts';

import type { XYChart } from '@amcharts/amcharts4/charts';
/**
 * Create a vertical bar chart.
 *
 * @param chartData The chart data
 * @param element The html element to render the chart in
 * @returns a promise with the chart reference
 */
export function createVerticalBarChart(chartData: AmChartsFrequencySetData | null | undefined, element: HTMLElement | undefined) {
  return new Promise<XYChart | undefined>((resolve) => {
    // Lazy load amcharts
    Promise.all([
      import(/* webpackChunkName: "am4core" */ '@amcharts/amcharts4/core'),
      import(/* webpackChunkName: "am4charts" */ '@amcharts/amcharts4/charts'),
    ])
      .then((modules) => {
        if (chartData) {
          const am4core = modules[0];
          const am4charts = modules[1];

          const chart = am4core.create(element, am4charts.XYChart);

          // Automatically set height of chart based on number of columns
          const cellSize = 30;
          chart.events.on('datavalidated', function (ev) {
            // Get objects of interest
            const chart = ev.target;
            const categoryAxis = chart.yAxes.getIndex(0);

            if (categoryAxis) {
              // Calculate how we need to adjust chart height
              const adjustHeight = chart.data.length * cellSize - categoryAxis.pixelHeight;

              // get current chart height
              const targetHeight = chart.pixelHeight + adjustHeight;

              // Set it on chart's container
              if (chart.svgContainer) {
                chart.svgContainer.htmlElement.style.height = targetHeight + 'px';
              }
            }
          });

          const dataProvider: {
            value: string;
            frequency: number;
          }[] = chartData.dataProvider;

          const categoryAxis = chart.yAxes.push(new am4charts.CategoryAxis());
          categoryAxis.renderer.grid.template.location = 0;
          categoryAxis.dataFields.category = 'value';
          categoryAxis.renderer.minGridDistance = 1;
          categoryAxis.renderer.inversed = true;
          categoryAxis.renderer.grid.template.disabled = true;
          // Truncate y-axis labels
          categoryAxis.renderer.labels.template.truncate = true;
          categoryAxis.renderer.labels.template.maxWidth = 120;

          const valueAxis = chart.xAxes.push(new am4charts.ValueAxis());
          valueAxis.min = 0;

          const series = chart.series.push(new am4charts.ColumnSeries());
          series.dataFields.categoryY = 'value';
          series.dataFields.valueX = 'frequency';
          series.columns.template.tooltipText = '{categoryY}: [bold]{valueX}[/]';
          series.columns.template.tooltipPosition = 'pointer'; // Tooltip follows mouse pointer
          series.columns.template.strokeOpacity = 0;
          series.columns.template.column.cornerRadiusBottomRight = 4;
          series.columns.template.column.cornerRadiusTopRight = 4;

          // Slant and align labels
          series.xAxis.renderer.labels.template.rotation = -60;
          series.xAxis.renderer.labels.template.horizontalCenter = 'right';
          // Rotate all labels except for the first column with '0';
          series.xAxis.renderer.labels.template.adapter.add('rotation', (rotation, target) =>
            target.dataItem?.label.text === '0' ? 0 : rotation
          );
          // Align all labels right except for the first column with '0';
          series.xAxis.renderer.labels.template.adapter.add('horizontalCenter', (horizontalCenter, target) =>
            target.dataItem?.label.text === '0' ? 'middle' : horizontalCenter
          );

          // Values in the bars
          const labelBullet = series.bullets.push(new am4charts.LabelBullet());
          labelBullet.label.horizontalCenter = 'left';
          labelBullet.label.dx = 10;
          labelBullet.label.text = "{values.valueX.workingValue.formatNumber('#.0as')}";
          labelBullet.locationX = 1;
          labelBullet.label.truncate = false; // Always see value

          // as by default columns of the same series are of the same color, we add adapter which takes colors from chart.colors color set
          series.columns.template.adapter.add('fill', (_, target) => {
            return chart?.colors.getIndex(target?.dataItem?.index || 0);
          });

          categoryAxis.sortBySeries = series;

          chart.data = dataProvider;

          resolve(chart);
        }
      })
      .catch((e) => {
        console.error('Error when creating chart', e);
        resolve(undefined);
      });
  });
}
